/**
 * useSettingsForm
 *
 * Comprehensive hook managing the campaign settings wizard state, including
 * form fields, survey questions, media, theming, and step navigation.
 */
import { useState, useEffect } from 'react';
import { useToast } from '../components/ui/notifications/toast-notification.jsx';
import { useNavigate } from 'react-router-dom';
import { useNamespace } from '../context/namespace';
import { useFormCache } from './useFormCache';

export const useSettingsForm = (campaignData) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentNamespace, userPermission } = useNamespace();
  
  const initialFormData = {
    name: '',
    title: '',
    description: '',
    businessName: '',
    website: '',
    email: '',
    phone: ''
  };
  
  // Form state
  const [formData, updateFormData, clearFormCache] = useFormCache(initialFormData);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [aiGeneratedFields, setAiGeneratedFields] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [hasExplainerVideo, setHasExplainerVideo] = useState(false);
  const [explainerVideo, setExplainerVideo] = useState(null);
  
  // Theme state
  const [selectedTheme, setSelectedTheme] = useState('sunset');
  const [gradientColors, setGradientColors] = useState({
    from: '#1a365d',
    via: '#3182ce',
    to: '#2c5282'
  });
  const [gradientDirection, setGradientDirection] = useState('br'); // br = bottom-right
  const [hexText, setHexText] = useState('#ffffff');
  
  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [showPreview, setShowPreview] = useState(true);
  
  // Populate form with campaign data when available
  useEffect(() => {
    if (campaignData) {
      // Update form data with campaign data
      updateFormData({
        name: campaignData.name || '',
        title: campaignData.title || '',
        description: campaignData.description || '',
        businessName: campaignData.businessName || '',
        website: campaignData.website || '',
        email: campaignData.email || '',
        phone: campaignData.phone || ''
      });
      
      // Set survey questions
      console.log('Setting survey questions from:', campaignData.surveyQuestions, 'type:', typeof campaignData.surveyQuestions);
      
      if (campaignData.surveyQuestions) {
        let parsedQuestions = [];
        
        // Handle case where surveyQuestions is a string (JSON)
        if (typeof campaignData.surveyQuestions === 'string') {
          try {
            // Parse the JSON string to get the array of questions
            parsedQuestions = JSON.parse(campaignData.surveyQuestions);
            
            // Check if parsedQuestions is still a string after parsing (double-encoded JSON)
            if (typeof parsedQuestions === 'string') {
              try {
                // Try to parse it again to handle double-encoded JSON
                parsedQuestions = JSON.parse(parsedQuestions);
                console.log('Successfully parsed double-encoded survey questions:', parsedQuestions);
              } catch (innerError) {
                console.error('Error parsing double-encoded surveyQuestions string:', innerError);
                // Treat it as a single question if it can't be parsed
                parsedQuestions = [parsedQuestions];
              }
            } else {
              console.log('Successfully parsed survey questions from string:', parsedQuestions);
            }
          } catch (error) {
            console.error('Error parsing surveyQuestions string:', error);
            // If can't parse, use it as a single question string
            parsedQuestions = [campaignData.surveyQuestions];
          }
        } 
        // Handle case where surveyQuestions is already an array
        else if (Array.isArray(campaignData.surveyQuestions)) {
          parsedQuestions = campaignData.surveyQuestions;
          console.log('Survey questions already in array format:', parsedQuestions);
        }
        
        // Map the questions to the required format
        if (parsedQuestions && parsedQuestions.length > 0) {
          const formattedQuestions = parsedQuestions.map((q, index) => ({
            id: index + 1,
            question: typeof q === 'string' ? q : q.question || ''
          }));
          
          console.log('Setting formatted survey questions:', formattedQuestions);
          setSurveyQuestions(formattedQuestions);
        } else {
          console.log('No valid survey questions found, setting empty array');
          setSurveyQuestions([]);
        }
      } else {
        console.log('No surveyQuestions property in campaignData, setting empty array');
        setSurveyQuestions([]);
      }
      
      // Set theme
      if (campaignData.theme) {
        setSelectedTheme(campaignData.theme);
      }
      
      // Set gradient colors if available
      if (campaignData.gradientColors) {
        setGradientColors(campaignData.gradientColors);
      }
      
      // Set gradient direction if available
      if (campaignData.gradientDirection) {
        setGradientDirection(campaignData.gradientDirection);
      }
      
      // Set text color if available
      if (campaignData.textColor) {
        setHexText(campaignData.textColor);
      }
      
      // Set preview image if available
      if (campaignData.campaignImage) {
        console.log('Setting preview image from campaignData.campaignImage:', campaignData.campaignImage);
        setPreviewImage(campaignData.campaignImage);
      }
      
      // Set explainer video if available
      if (campaignData.videoUrl || campaignData.explainerVideo) {
        console.log('Setting explainer video:', campaignData.explainerVideo || campaignData.videoUrl);
        setHasExplainerVideo(true);
        setExplainerVideo(campaignData.explainerVideo || campaignData.videoUrl);
      } else if (campaignData.hasExplainerVideo && campaignData.explainerVideo) {
        console.log('Setting explainer video from hasExplainerVideo flag:', campaignData.explainerVideo);
        setHasExplainerVideo(true);
        setExplainerVideo(campaignData.explainerVideo);
      }
    }
  }, [campaignData, updateFormData]);
  
  // Input handlers
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    
    // When user edits a field, remove it from AI generated fields
    if (aiGeneratedFields[id]) {
      setAiGeneratedFields(prev => ({
        ...prev,
        [id]: false
      }));
    }
    
    updateFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleAddQuestion = () => {
    // Limit to a maximum of 3 questions
    if (surveyQuestions.length >= 3) {
      return;
    }
    
    setSurveyQuestions([
      ...surveyQuestions,
      { id: surveyQuestions.length + 1, question: '' }
    ]);
  };

  const handleRemoveQuestion = (id) => {
    setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
  };

  const handleQuestionChange = (id, value) => {
    // Check if this question was AI generated
    const questionIndex = surveyQuestions.findIndex(q => q.id === id);
    const fieldName = `question_${questionIndex}`;
    
    // When user edits a question, remove it from AI generated fields
    if (aiGeneratedFields[fieldName]) {
      setAiGeneratedFields(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
    
    setSurveyQuestions(
      surveyQuestions.map(q => q.id === id ? { ...q, question: value } : q)
    );
  };

  const handleImageChange = (file, setError) => {
    console.log('useSettingsForm.handleImageChange called with:', file);
    
    if (file) {
      console.log('File selected:', file);
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        console.error('File too large:', file.size);
        if (setError) {
          setError({
            type: 'error',
            message: 'Image size should be less than 5MB'
          });
          setTimeout(() => setError(null), 3000);
        }
        return;
      }
      
      const reader = new FileReader();
      reader.onloadstart = () => {
        console.log('Starting to load image in useSettingsForm');
      };
      
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          console.log(`useSettingsForm: Loading progress: ${progress}%`);
        }
      };
      
      reader.onloadend = () => {
        console.log('useSettingsForm: Image loaded successfully');
        setPreviewImage(reader.result);
        console.log('useSettingsForm: previewImage set to:', reader.result ? 'image data' : 'null');
      };
      
      reader.onerror = (error) => {
        console.error('useSettingsForm: Error reading file:', error);
        if (setError) {
          setError({
            type: 'error',
            message: 'Error reading image file. Please try again.'
          });
          setTimeout(() => setError(null), 3000);
        }
      };
      
      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('useSettingsForm: Exception while reading file:', error);
        if (setError) {
          setError({
            type: 'error',
            message: 'Error processing image file. Please try again.'
          });
          setTimeout(() => setError(null), 3000);
        }
      }
    }
  };

  const handleRemoveImage = () => {
    console.log('Removing campaign image - setting previewImage to null');
    setPreviewImage(null);
  };

  const handleNext = (e) => {
    // Prevent form submission if an event is passed
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (currentStep < 6) { // 6 is the last step index
      // Skip category and subcategory steps in settings flow
      if (currentStep === 0) {
        // Skip directly to design step (step 3)
        setCurrentStep(3);
        setShowPreview(true);
        return;
      }
      
      // Normal case - go to next step
      setCurrentStep(currentStep + 1);
      // Always show preview
      setShowPreview(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // If going back from design step (step 3), go directly to internal name (step 0)
      if (currentStep === 3) {
        setCurrentStep(0);
      }
      // Normal case - go back one step
      else {
        setCurrentStep(currentStep - 1);
      }
      
      // Always show preview
      setShowPreview(true);
    }
  };

  const resetForm = () => {
    if (campaignData) {
      // Reset to original campaign data
      updateFormData({
        name: campaignData.name || '',
        title: campaignData.title || '',
        description: campaignData.description || '',
        businessName: campaignData.businessName || '',
        website: campaignData.website || '',
        email: campaignData.email || '',
        phone: campaignData.phone || ''
      });
      
      // Reset survey questions
      if (campaignData.surveyQuestions && Array.isArray(campaignData.surveyQuestions) && campaignData.surveyQuestions.length > 0) {
        setSurveyQuestions(campaignData.surveyQuestions.map((q, index) => ({
          id: index + 1,
          question: typeof q === 'string' ? q : q.question || ''
        })));
      } else {
        setSurveyQuestions([]);
      }
      
      // Reset theme
      if (campaignData.theme) {
        setSelectedTheme(campaignData.theme);
      } else {
        setSelectedTheme('sunset');
      }
      
      // Reset gradient colors
      if (campaignData.gradientColors) {
        setGradientColors(campaignData.gradientColors);
      } else {
        setGradientColors({
          from: '#1a365d',
          via: '#3182ce',
          to: '#2c5282'
        });
      }
      
      // Reset gradient direction
      if (campaignData.gradientDirection) {
        setGradientDirection(campaignData.gradientDirection);
      } else {
        setGradientDirection('br');
      }
      
      // Reset text color
      if (campaignData.textColor) {
        setHexText(campaignData.textColor);
      } else {
        setHexText('#ffffff');
      }
      
      // Reset preview image
      if (campaignData.campaignImage) {
        console.log('Setting preview image from campaignData.campaignImage:', campaignData.campaignImage);
        setPreviewImage(campaignData.campaignImage);
      } else {
        setPreviewImage(null);
      }
      
      // Reset explainer video
      if (campaignData.videoUrl || campaignData.explainerVideo) {
        console.log('Setting explainer video:', campaignData.explainerVideo || campaignData.videoUrl);
        setHasExplainerVideo(true);
        setExplainerVideo(campaignData.explainerVideo || campaignData.videoUrl);
      } else if (campaignData.hasExplainerVideo && campaignData.explainerVideo) {
        console.log('Setting explainer video from hasExplainerVideo flag:', campaignData.explainerVideo);
        setHasExplainerVideo(true);
        setExplainerVideo(campaignData.explainerVideo);
      } else {
        setHasExplainerVideo(false);
        setExplainerVideo(null);
      }
    } else {
      // If no campaign data, reset to initial state
      updateFormData(initialFormData);
      setSurveyQuestions([]);
      setPreviewImage(null);
      setCurrentStep(0);
      setAiGeneratedFields({});
    }
  };

  return {
    formData,
    updateFormData,
    clearFormCache,
    surveyQuestions,
    setSurveyQuestions,
    aiGeneratedFields,
    setAiGeneratedFields,
    previewImage,
    setPreviewImage,
    hasExplainerVideo,
    setHasExplainerVideo,
    explainerVideo,
    setExplainerVideo,
    selectedTheme,
    setSelectedTheme,
    gradientColors,
    setGradientColors,
    gradientDirection,
    setGradientDirection,
    hexText,
    setHexText,
    currentStep,
    setCurrentStep,
    showPreview,
    setShowPreview,
    handleInputChange,
    handleAddQuestion,
    handleRemoveQuestion,
    handleQuestionChange,
    handleImageChange,
    handleRemoveImage,
    handleNext,
    handlePrevious,
    resetForm,
    currentNamespace,
    userPermission
  };
}; 